import { isIos, isStandalone } from "./push.ts";

/**
 * iPhone/iPad Home Screen app: following a download link makes the app's own window show iOS's file preview, which
 * has no way back (no browser chrome in a standalone app) — the app had to be killed. Download links are caught
 * instead: the file is fetched with the session, then a small sheet offers "Save or share", which opens the native
 * share sheet (Save to Files, AirDrop, apps). A second tap is needed because iOS only shares from a user gesture.
 */
export function installIosDownloads(): void {
  if (!isIos() || !isStandalone()) return;
  document.addEventListener("click", onClick, true);
}

const isDownload = (a: HTMLAnchorElement) =>
  a.hasAttribute("download") || /[?&]download=1(&|$)/.test(a.href) || /\/snapshots\/[^/]+\/download(\?|$)/.test(a.href);

function onClick(e: MouseEvent) {
  const a = (e.target as Element | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
  if (!a || !isDownload(a)) return;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin && url.protocol !== "blob:" && url.protocol !== "data:") return; // other sites: leave to iOS
  e.preventDefault();
  e.stopPropagation();
  void start(url.href, a.getAttribute("download") || "");
}

const ar = () => document.documentElement.lang.startsWith("ar");
const T = {
  preparing: () => (ar() ? "جارٍ تجهيز الملف…" : "Preparing the file…"),
  share: () => (ar() ? "حفظ أو مشاركة" : "Save or share"),
  close: () => (ar() ? "إغلاق" : "Close"),
  failed: () => (ar() ? "تعذّر تنزيل الملف." : "Could not download the file."),
};

function fileNameFrom(res: Response, fallback: string, url: string): string {
  const cd = res.headers.get("content-disposition") ?? "";
  const star = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
  if (star) { try { return decodeURIComponent(star); } catch { /* fall through */ } }
  const plain = /filename="?([^";]+)"?/i.exec(cd)?.[1];
  if (plain) return plain;
  if (fallback) return fallback;
  const path = new URL(url).searchParams.get("path");
  return (path ?? new URL(url).pathname).split("/").filter(Boolean).pop() || "download";
}

const fmtSize = (n: number) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

async function start(url: string, suggested: string) {
  const sheet = makeSheet();
  sheet.status(T.preparing());
  try {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error(String(res.status));
    const blob = await res.blob();
    const name = fileNameFrom(res, suggested, url);
    const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
    sheet.ready(name, fmtSize(blob.size), async () => {
      try {
        if (navigator.canShare?.({ files: [file] })) await navigator.share({ files: [file] });
        else window.open(URL.createObjectURL(file), "_blank"); // very old iOS: best effort
        sheet.close();
      } catch (err) {
        if ((err as Error)?.name !== "AbortError") sheet.status(T.failed());
      }
    });
  } catch {
    sheet.status(T.failed());
  }
}

function makeSheet() {
  const root = document.createElement("div");
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-modal", "true");
  root.style.cssText = "position:fixed;inset:0;z-index:200;display:flex;align-items:flex-end;justify-content:center;background:rgba(0,0,0,.55);padding:16px;padding-bottom:max(16px,env(safe-area-inset-bottom))";
  const card = document.createElement("div");
  card.dir = ar() ? "rtl" : "ltr";
  card.style.cssText = "width:100%;max-width:420px;border-radius:18px;background:var(--color-surface,#181c21);color:var(--color-fg,#eef1f4);border:1px solid var(--color-border,#2a3038);padding:18px;font:inherit;box-shadow:0 20px 60px rgba(0,0,0,.5)";
  const title = document.createElement("div");
  title.style.cssText = "font-weight:600;word-break:break-all;direction:ltr;text-align:start";
  const sub = document.createElement("div");
  sub.style.cssText = "margin-top:4px;font-size:13px;opacity:.7";
  const row = document.createElement("div");
  row.style.cssText = "display:flex;gap:8px;margin-top:16px";
  const btn = (label: string, primary: boolean) => {
    const b = document.createElement("button");
    b.type = "button";
    b.textContent = label;
    b.style.cssText = `flex:1;min-height:46px;border-radius:12px;font:inherit;font-weight:600;border:1px solid ${primary ? "transparent" : "var(--color-border,#2a3038)"};background:${primary ? "var(--color-accent,#3ccf9f)" : "transparent"};color:${primary ? "#05231a" : "inherit"}`;
    return b;
  };
  const closeBtn = btn(T.close(), false);
  card.append(title, sub, row);
  row.append(closeBtn);
  root.append(card);
  const close = () => root.remove();
  closeBtn.onclick = close;
  root.onclick = (e) => { if (e.target === root) close(); };
  document.body.append(root);
  return {
    status(text: string) { title.textContent = text; sub.textContent = ""; },
    ready(name: string, size: string, onShare: () => void) {
      title.textContent = name;
      sub.textContent = size;
      const share = btn(T.share(), true);
      share.onclick = onShare;
      row.prepend(share);
    },
    close,
  };
}
